import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { LOGO, STEM_HERO } from "@/lib/assets";
import { STEM_CATEGORIES, ytThumb, ytEmbed } from "@/lib/stemVideos";
import { Play, X, ArrowLeft, FlaskConical } from "lucide-react";

export default function StemLibrary() {
  const { t, lang, toggle } = useLang();
  const s = t.stem;
  const navigate = useNavigate();
  const [cat, setCat] = useState("sciences");
  const [active, setActive] = useState(null);
  const [all, setAll] = useState([]);

  useEffect(() => { api.get("/stem-videos").then((r) => setAll(r.data)).catch(() => {}); }, []);

  const videos = all.filter((v) => v.category === cat);

  return (
    <div className="min-h-screen bg-icdh-navy text-icdh-white">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-icdh-navy/80 backdrop-blur-xl border-b border-icdh-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="stem-logo" className="flex items-center gap-3">
            <img src={LOGO} alt="ICDH" className="h-10 w-10 object-contain bg-white rounded-full p-1" />
            <span className="font-extrabold tracking-tight">ICDH · STEM</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggle} data-testid="stem-lang-toggle" className="h-10 px-4 border border-icdh-white/25 text-xs font-bold tracking-[0.15em] hover:bg-icdh-white hover:text-icdh-navy transition-colors">{lang === "fr" ? "EN" : "FR"}</button>
            <Link to="/" data-testid="stem-back" className="h-10 px-4 inline-flex items-center gap-2 text-sm hover:text-icdh-red transition-colors"><ArrowLeft size={16} />{s.back}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img src={STEM_HERO} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-icdh-navy via-icdh-navy/70 to-icdh-navy/40" />
        </div>
        <div className="noise-overlay absolute inset-0 opacity-[0.05]" />
        <div className="relative mx-auto max-w-[1400px] w-full px-6 md:px-10 pb-16">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-icdh-red mb-6">
            <FlaskConical size={16} />{s.overline}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-4xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl">{s.title}</motion.h1>
          <p className="mt-6 max-w-2xl text-icdh-white/70 text-lg">{s.lead}</p>
        </div>
      </section>

      {/* Category tabs */}
      <div className="sticky top-[68px] z-40 bg-icdh-navy/90 backdrop-blur-md border-y border-icdh-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex gap-2 overflow-x-auto py-3">
          {STEM_CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`stem-tab-${c.key}`}
              className={`whitespace-nowrap px-5 h-10 text-sm font-medium transition-colors ${cat === c.key ? "bg-icdh-red text-white" : "border border-icdh-white/20 text-icdh-white/80 hover:bg-icdh-white/10"}`}>
              {c.label[lang]}
            </button>
          ))}
        </div>
      </div>

      {/* Video grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((v, i) => (
            <motion.button key={v.id} onClick={() => setActive(v)} data-testid={`stem-video-${i}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="group text-left border border-icdh-white/10 hover:border-icdh-red transition-colors duration-500">
              <div className="relative overflow-hidden aspect-video">
                <img src={ytThumb(v.video_id)} alt={v.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-icdh-navy/30 group-hover:bg-icdh-navy/10 transition-colors flex items-center justify-center">
                  <span className="h-16 w-16 rounded-full bg-icdh-red flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-xl">
                    <Play size={26} className="text-white ml-1" fill="white" />
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-sans font-bold text-lg tracking-tight">{v.title}</h3>
                <p className="mt-1 text-icdh-white/50 text-sm">{v.author}</p>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-16 text-center">
          <button onClick={() => navigate("/#admissions")} data-testid="stem-enroll"
            className="h-14 px-10 bg-amber-500 hover:bg-amber-600 text-icdh-navy text-sm font-bold uppercase tracking-[0.14em] transition-colors">
            {s.cta_enroll}
          </button>
        </div>
      </section>

      {/* Player modal */}
      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="stem-player" className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-icdh-red">{s.now_playing}</p>
                  <p className="font-serif text-xl md:text-2xl">{active.title}</p>
                </div>
                <button onClick={() => setActive(null)} data-testid="stem-player-close" className="h-11 w-11 flex items-center justify-center bg-icdh-white/10 hover:bg-icdh-red transition-colors"><X /></button>
              </div>
              <div className="relative aspect-video bg-black">
                <iframe title={active.title} src={ytEmbed(active.video_id)} className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
